<div class="dashboard-container">
  <main class="dashboard-content">
    <div class="top-grid" [class.top-grid--single]="showUnconfiguredHome() && !hasHomeActions()">
      @if (showUnconfiguredHome()) {
        <section class="home-unconfigured" aria-live="polite">
          <div class="home-unconfigured-icon" aria-hidden="true">
            <lucide-icon name="LayoutGrid" [size]="28"></lucide-icon>
          </div>
          <h2>Parece que aún no hay nada configurado</h2>
          <p>
            No es un error. Todavía no hay liquidaciones ni lecturas que mostrar. Cuando estén
            disponibles, aparecerán aquí.
          </p>
        </section>
      } @else {
        <section
          class="card main-kpi"
          [class.main-kpi--compact-hero]="!hasBilling() && showComparison() && hasComparisonData()"
        >
          <div class="kpi-hero">
            <div
              class="kpi-graph-placeholder"
              [class.is-comparison-active]="showComparison() && hasComparisonData()"
            >
              @if (hasBilling()) {
                <div [hidden]="showComparison()" #chartContainer class="kpi-chart-container"></div>
                @if (isKpiLoading() || isChartLoading()) {
                  <div class="kpi-chart-skeleton" aria-hidden="true">
                    <p-skeleton width="100%" height="100%" borderRadius="4px" />
                  </div>
                }
              }
              @if (hasComparisonData() && (showComparison() || !hasBilling())) {
                <div class="comparison-card">
                  <div class="comparison-values">
                    <div class="value-box secondary">
                      <div class="box-top">
                        <div class="icon-wrapper secondary-icon">
                          <lucide-icon [name]="'check'" [size]="18" [style.color]="'#64748b'">
                          </lucide-icon>
                        </div>
                        <div class="trend neutral">
                          {{ comparisonPeriodBadgeLabel() }}
                        </div>
                      </div>

                      <span class="label">{{ comparisonPreviousConsumptionLabel() }}</span>

                      <h2>
                        {{ comparisonFormatReading(comparisonLiquidatedConsumption()) }}
                        <span class="unit">{{ comparisonUnits() }}</span>
                      </h2>

                      <small>
                        {{ formatPeriodRange(activeComparison()?.liquidatedPeriod) }}
                      </small>

                      @if (comparisonLiquidatedConsumption() != null) {
                        <div class="progress-line secondary-progress">
                          <div
                            class="progress-fill"
                            [style.width.%]="
                              comparisonProgressPercent(comparisonLiquidatedConsumption())
                            "
                          ></div>
                        </div>
                      }
                    </div>

                    <div class="value-box primary">
                      <div class="box-top">
                        @if (activeSupplyTypeId(); as supplyId) {
                          <div class="icon-wrapper" [style.background]="getColorSupplies(supplyId)">
                            <lucide-icon
                              [name]="getLucideIconSupplies(supplyId)"
                              [size]="18"
                              [style.color]="'#f8fafc'"
                            >
                            </lucide-icon>
                          </div>
                        }
                        <div class="trend neutral supply-name-badge">
                          {{ activeComparison()?.supplyType?.name }}
                        </div>
                      </div>

                      <span class="label"> Consumo actual </span>
                      <h2>
                        {{ comparisonFormatReading(comparisonCurrentConsumption()) }}
                        <span class="unit">{{ comparisonUnits() }}</span>
                      </h2>

                      <small>
                        @if (activeComparison()?.lastDailyReadingDate) {
                          {{ formatApiDate(activeComparison()?.lastDailyReadingDate) }}
                        } @else {
                          Fecha no disponible
                        }
                      </small>

                      @if (comparisonCurrentConsumption() != null) {
                        <div class="progress-line">
                          @if (activeSupplyTypeId(); as supplyId) {
                            <div
                              class="progress-fill"
                              [style.background]="getColorSupplies(supplyId)"
                              [style.width.%]="
                                comparisonProgressPercent(comparisonCurrentConsumption())
                              "
                            ></div>
                          } @else {
                            <div
                              class="progress-fill"
                              [style.width.%]="
                                comparisonProgressPercent(comparisonCurrentConsumption())
                              "
                            ></div>
                          }
                        </div>
                      }
                    </div>
                  </div>

                  <div class="comparison-summary">
                    <div class="comparison-summary-block">
                      <span class="comparison-summary-label">Periodo actual</span>
                      <span class="comparison-summary-period">
                        {{ formatPeriodRange(activeComparison()?.nextPeriodToLiquidate) }}
                      </span>
                    </div>
                    <div class="comparison-summary-block comparison-summary-block--estimate">
                      <span class="comparison-summary-label">Estimado al cierre</span>
                      <div class="comparison-summary-estimate-row">
                        <span class="comparison-summary-value">
                          {{ comparisonFormatReading(comparisonEstimatedConsumption()) }}
                          {{ comparisonUnits() }}
                        </span>
                        <span class="trend" [class]="comparisonTrendClass()">
                          {{ comparisonTrendLabel() }}
                        </span>
                      </div>
                    </div>
                  </div>

                  @if (comparisonItems().length > 1) {
                    <div class="comparison-paginator">
                      <button
                        type="button"
                        class="comparison-nav-btn"
                        [disabled]="comparisonIndex() === 0"
                        (click)="previousComparison()"
                        aria-label="Suministro anterior"
                      >
                        <lucide-icon name="chevron-left" [size]="18"></lucide-icon>
                      </button>
                      <div class="comparison-dots" role="tablist" [attr.aria-label]="'Suministros'">
                        @for (item of comparisonItems(); track item.supplyType.id; let i = $index) {
                          <button
                            type="button"
                            class="comparison-dot"
                            [class.active]="comparisonIndex() === i"
                            (click)="goToComparison(i)"
                            [attr.aria-label]="item.supplyType.name"
                            [attr.aria-selected]="comparisonIndex() === i"
                          ></button>
                        }
                      </div>
                      <button
                        type="button"
                        class="comparison-nav-btn"
                        [disabled]="comparisonIndex() === comparisonItems().length - 1"
                        (click)="nextComparison()"
                        aria-label="Suministro siguiente"
                      >
                        <lucide-icon name="chevron-right" [size]="18"></lucide-icon>
                      </button>
                    </div>
                  }
                </div>
              }
            </div>

            <div
              class="kpi-hero-content"
              [class.kpi-hero-content--comparison]="showComparison()"
              [hidden]="isKpiLoading()"
            >
              @if (hasBilling()) {
                @if (!showComparison()) {
                  <h2>
                    Última liquidación
                    <span
                      class="custom-tooltip-trigger"
                      data-tooltip="Es el importe calculado en base a tu consumo de Agua fría en el periodo indicado."
                    >
                      <i class="info-icon-circle">i</i>
                    </span>
                  </h2>
                }
                @if (lastSettlement(); as settlement) {
                  <div class="d-flex justify-content-between">
                    @if (!showComparison() || comparisonItems().length > 1) {
                      <span class="info-tag">
                        @if (showComparison() && activeComparison(); as comparison) {
                          {{ comparison.supplyType.name }}
                        } @else {
                          {{ lastSettlement()?.supplyType }}
                        }
                      </span>
                    }

                    @if (hasComparisonData()) {
                      <span (click)="showComparisonF()" class="cursor-pointer info-tag">
                        @if (showComparison()) {
                          Mostrar gráfica
                        } @else {
                          Mostrar comparativa
                        }
                      </span>
                    }
                  </div>
                }
              } @else if (hasComparisonData() && comparisonItems().length > 1) {
                @if (activeComparison(); as comparison) {
                  <div class="d-flex justify-content-between">
                    <span class="info-tag">{{ comparison.supplyType.name }}</span>
                  </div>
                }
              }
            </div>

            @if (isKpiLoading()) {
              <div class="kpi-hero-content kpi-hero-skeleton" aria-busy="true">
                <p-skeleton width="55%" height="20px" borderRadius="6px" />
                <div class="d-flex justify-content-between mt-2">
                  <p-skeleton width="90px" height="28px" borderRadius="999px" />
                  <p-skeleton width="90px" height="28px" borderRadius="999px" />
                </div>
              </div>
            }
          </div>

          @if (isKpiLoading()) {
            <div class="kpi-body" aria-busy="true">
              <div class="period">
                <div>
                  <p-skeleton width="100%" height="12px" />
                  <p-skeleton width="70%" height="12px" styleClass="mt-2" />
                </div>
                <p-skeleton width="120px" height="32px" />
              </div>
              <p-skeleton width="220px" height="34px" borderRadius="4px" styleClass="mt-1" />
            </div>
          } @else if (showComparison() && hasComparisonData()) {
            @if (hasBilling() && lastSettlement(); as settlement) {
              <div class="kpi-body kpi-body-comparison">
                <p-button
                  label="Descargar última liquidación (PDF)"
                  icon="pi pi-download"
                  size="small"
                  styleClass="kpi-download-btn"
                  (onClick)="downloadPdf(settlement)"
                  [loading]="isDownloading()"
                  [disabled]="isDownloading()"
                />
              </div>
            }
          } @else if (hasBilling()) {
            @if (lastSettlement(); as settlement) {
              <div class="kpi-body">
                <div class="period">
                  <div>
                    <label>Periodo liquidado</label>
                    <span>
                      {{ dateSpanishFormat(lastSettlement()?.initDate) }} -
                      {{ dateSpanishFormat(lastSettlement()?.endDate) }}
                    </span>
                  </div>

                  <div class="amount">{{ spanishFormat(lastSettlement()?.totalAmount) }} €</div>
                </div>

                <p-button
                  label="Descargar liquidación (PDF)"
                  icon="pi pi-download"
                  size="small"
                  styleClass="kpi-download-btn"
                  (onClick)="downloadPdf(settlement)"
                  [loading]="isDownloading()"
                  [disabled]="isDownloading()"
                />
              </div>
            }
          }
        </section>
      }

      @if (!showUnconfiguredHome() || hasHomeActions()) {
        <div class="action-grid">
          @if (actionsReady()) {
            @if (hasBilling()) {
              <div class="premium-action-card" [routerLink]="['/consumos']">
                <div class="card-inner-content">
                  <div class="icon-section">
                    <div class="icon-orb" [style.color]="'#134B70'">
                      <lucide-icon [name]="'BarChart3'" [strokeWidth]="2" [size]="24"></lucide-icon>
                      <div class="orb-ring"></div>
                    </div>
                  </div>

                  <div class="text-section">
                    <div class="text-header">
                      <span class="category-tag">Consumos y liquidaciones</span>
                      <h3 class="main-title">Control de consumos</h3>
                    </div>
                    <p class="description-text">
                      <span class="description-copy description-copy--desktop">
                        Visualiza los consumos de tus contadores, consulta su histórico y compara la
                        evolución del consumo en diferentes períodos.
                      </span>
                      <span class="description-copy description-copy--mobile">
                        Consulta tu histórico de consumo.
                      </span>
                    </p>
                  </div>

                  <div class="action-indicator">
                    <div class="arrow-circle">
                      <lucide-icon [name]="'ArrowRight'" [size]="16"></lucide-icon>
                    </div>
                  </div>
                </div>
              </div>
            }

            @if (showDailyReadingsCard()) {
              <div class="premium-action-card" [routerLink]="['/lecturas-diarias']">
                <div class="card-inner-content">
                  <div class="icon-section">
                    <div class="icon-orb" [style.color]="'#134B70'">
                      <lucide-icon
                        [name]="'CalendarRange'"
                        [strokeWidth]="2"
                        [size]="24"
                      ></lucide-icon>
                      <div class="orb-ring"></div>
                    </div>
                  </div>

                  <div class="text-section">
                    <div class="text-header">
                      <span class="category-tag">Monitorización</span>
                      <h3 class="main-title">Lecturas diarias</h3>
                    </div>
                    <p class="description-text">
                      <span class="description-copy description-copy--desktop">
                        Consulta las lecturas diarias de tus contadores radio inteligentes
                      </span>
                      <span class="description-copy description-copy--mobile">
                        Consulta las lecturas diarias de tus contadores
                      </span>
                    </p>
                  </div>

                  <div class="action-indicator">
                    <div class="arrow-circle">
                      <lucide-icon [name]="'ArrowRight'" [size]="16"></lucide-icon>
                    </div>
                  </div>
                </div>
              </div>
            }

            @if (homeData()?.hasAlarmModuleAccess) {
              <div class="premium-action-card" [routerLink]="['/alarmas']">
                <div class="card-inner-content">
                  <div class="icon-section">
                    <div class="icon-orb" [style.color]="'#E67E22'">
                      <lucide-icon [name]="'BarChart3'" [strokeWidth]="2" [size]="24"></lucide-icon>
                      <div class="orb-ring"></div>
                    </div>
                  </div>

                  <div class="text-section">
                    <div class="text-header">
                      <span class="category-tag">Alarmas</span>
                      <h3 class="main-title">Centro de alertas</h3>
                    </div>
                    <p class="description-text">
                      <span class="description-copy description-copy--desktop">
                        Consulta las alertas inteligentes asociadas a tus contadores
                      </span>
                      <span class="description-copy description-copy--mobile">
                        Consulta las alertas inteligentes de los contadores
                      </span>
                    </p>
                  </div>

                  <div class="action-indicator">
                    <div class="arrow-circle">
                      <lucide-icon [name]="'ArrowRight'" [size]="16"></lucide-icon>
                    </div>
                  </div>
                </div>
              </div>
            }
            @if (homeData()?.hasBilling) {
              <div class="premium-action-card" [routerLink]="['/liquidaciones']">
                <div class="card-inner-content">
                  <div class="icon-section">
                    <div class="icon-orb" [style.color]="'#2C3E50'">
                      <lucide-icon
                        [name]="'ReceiptText'"
                        [strokeWidth]="2"
                        [size]="24"
                      ></lucide-icon>
                      <div class="orb-ring"></div>
                    </div>
                  </div>

                  <div class="text-section">
                    <div class="text-header">
                      <span class="category-tag">Consumos y liquidaciones</span>
                      <h3 class="main-title">Liquidaciones</h3>
                    </div>
                    <p class="description-text">
                      <span class="description-copy description-copy--desktop">
                        Consulta importes, compara periodos y descarga las liquidaciones
                        registradas.
                      </span>
                      <span class="description-copy description-copy--mobile">
                        Consulta los importes y descarga las liquidaciones.
                      </span>
                    </p>
                  </div>

                  <div class="action-indicator">
                    <div class="arrow-circle">
                      <lucide-icon [name]="'ArrowRight'" [size]="16"></lucide-icon>
                    </div>
                  </div>
                </div>
              </div>
            }

            @if (sendRequestsEnabled() && !showReciboVerdeNudge()) {
              <div class="premium-action-card" [routerLink]="['/enviar-consulta']">
                <div class="card-inner-content">
                  <div class="icon-section">
                    <div class="icon-orb" [style.color]="'#508C91'">
                      <lucide-icon
                        [name]="'MessageSquareMore'"
                        [strokeWidth]="2"
                        [size]="24"
                      ></lucide-icon>
                      <div class="orb-ring"></div>
                    </div>
                  </div>

                  <div class="text-section">
                    <div class="text-header">
                      <span class="category-tag">Atención y soporte</span>
                      <h3 class="main-title">Envíanos tu consulta</h3>
                    </div>
                    <p class="description-text">
                      <span class="description-copy description-copy--desktop">
                        Recibe asistencia personalizada para gestionar incidencias y consultas de tu
                        servicio
                      </span>
                      <span class="description-copy description-copy--mobile">
                        Gestionar consultas o incidencias
                      </span>
                    </p>
                  </div>

                  <div class="action-indicator">
                    <div class="arrow-circle">
                      <lucide-icon [name]="'ArrowRight'" [size]="16"></lucide-icon>
                    </div>
                  </div>
                </div>
              </div>
            }
            @if (showReciboVerdeNudge()) {
              <div
                class="premium-action-card"
                [routerLink]="['/recibo-verde']"
                [style.--primary-color]="'#00C95D'"
              >
                <div class="card-inner-content">
                  <div class="icon-section">
                    <div class="icon-orb" [style.color]="'#00C95D'">
                      <img
                        src="assets/img/logo_recibo_verde_sv_solo.svg"
                        alt=""
                        class="recibo-verde-orb-icon"
                        width="24"
                        height="22"
                      />
                      <div class="orb-ring"></div>
                    </div>
                  </div>

                  <div class="text-section">
                    <div class="text-header">
                      <span class="category-tag">Sostenibilidad</span>
                      <h3 class="main-title">Activa ReciboVerde</h3>
                    </div>
                    <p class="description-text">
                      <span class="description-copy description-copy--desktop">
                        Activa el servicio para recibir tus liquidaciones por email y reduce el uso
                        de papel
                      </span>
                      <span class="description-copy description-copy--mobile">
                        Recibe tus liquidaciones por email
                      </span>
                    </p>
                  </div>

                  <div class="action-indicator">
                    <div class="arrow-circle">
                      <lucide-icon [name]="'ArrowRight'" [size]="16"></lucide-icon>
                    </div>
                  </div>
                </div>
              </div>
            }

            @if (homeData()?.hasBilling) {
              <div class="premium-action-card" [routerLink]="['/enviar-lectura']">
                <div class="card-inner-content">
                  <div class="icon-section">
                    <div class="icon-orb" [style.color]="'#16423C'">
                      <lucide-icon [name]="'Camera'" [strokeWidth]="2" [size]="24"></lucide-icon>
                      <div class="orb-ring"></div>
                    </div>
                  </div>

                  <div class="text-section">
                    <div class="text-header">
                      <span class="category-tag">Registro manual</span>
                      <h3 class="main-title">Envía tu lectura</h3>
                    </div>
                    <p class="description-text">
                      <span class="description-copy description-copy--desktop">
                        Envía la lectura de tu contador para evitar estimaciones y mantener tu
                        consumo actualizado
                      </span>
                      <span class="description-copy description-copy--mobile">
                        Envía la lectura de tu contador para evitar estimaciones
                      </span>
                    </p>
                  </div>

                  <div class="action-indicator">
                    <div class="arrow-circle">
                      <lucide-icon [name]="'ArrowRight'" [size]="16"></lucide-icon>
                    </div>
                  </div>
                </div>
              </div>
            }
          } @else {
            @for (card of [1, 2, 3, 4]; track card) {
              <div
                class="premium-action-card skeleton-card"
                [attr.aria-busy]="card === 1 ? true : null"
                [attr.aria-hidden]="card === 1 ? null : true"
              >
                <div class="card-inner-content">
                  <p-skeleton shape="circle" size="24px" />
                  <p-skeleton width="40%" height="10px" />
                  <p-skeleton width="65%" height="16px" />
                  <p-skeleton width="100%" height="12px" />
                  <p-skeleton width="55%" height="12px" />
                  <div class="ms-auto">
                    <p-skeleton shape="circle" size="36px" />
                  </div>
                </div>
              </div>
            }
          }
        </div>
      }
    </div>

    @if (hasBilling()) {
      <div>
        @if (homeData()) {
          <p-card class="mt-3 mb-3 mt-xl-0 my-xl-0 home-table-card">
            <div class="d-flex fw-bold mb-2 fs-5 align-items-center">
              <div class="w-100">
                <span class="me-2"> Listado de liquidaciones</span>
                <p-button variant="text" (click)="op.toggle($event)" icon="pi pi-question-circle" />
                <!--                         <p-button class="ms-auto me-3" (click)="downloadSelectedSettlements()"
                            [disabled]="!settlementsSelecteds || settlementsSelecteds.length <= 0 || isDownloading()"
                            icon="pi pi-download" [label]="isDownloading() ? 'Descargando...' : 'Descargar'" /> -->
              </div>

              <!--             <div class="w-100 d-flex align-items-center justify-content-end">



                    </div>
 -->
            </div>

            <p-popover #op>
              <span
                >Puedes seleccionar/deseleccionar varias filas para descargar mediante click
                izquierdo.</span
              >
            </p-popover>
            @if (!tableInitialized()) {
              <div class="home-table-skeleton-overlay skeleton-table" aria-busy="true">
                <div class="skeleton-table-header" aria-hidden="true">
                  <p-skeleton width="60%" height="12px" />
                  <p-skeleton width="85%" height="12px" />
                  <p-skeleton width="75%" height="12px" />
                  <div class="d-flex justify-content-center w-100">
                    <p-skeleton width="70%" height="12px" />
                  </div>
                </div>
                @for (row of [1, 2, 3, 4, 5, 6]; track row) {
                  <div class="skeleton-table-row" aria-hidden="true">
                    <p-skeleton width="18px" height="18px" borderRadius="4px" />
                    <p-skeleton width="85%" height="12px" />
                    <p-skeleton width="75%" height="12px" />
                    <div class="d-flex justify-content-center align-items-center w-100">
                      <p-skeleton shape="circle" size="2.5rem" />
                    </div>
                  </div>
                }
              </div>
            }
            <p-table
              #settlementTable
              [value]="settlementsSignal()"
              selectionMode="multiple"
              [styleClass]="useFixedTableRowHeight() ? 'home-table-rows-fixed' : ''"
              [(selection)]="settlementsSelecteds"
              stripedRows
              [scrollable]="settlementsTableScrollable"
              [scrollHeight]="settlementsTableScrollHeight"
              [size]="'small'"
              [tableStyle]="settlementsTableStyle"
              (onLazyLoad)="loadSettlementsLazy($event)"
              [rowsPerPageOptions]="settlementsTableRowsPerPageOptions"
              [paginator]="true"
              [rows]="rows"
              [first]="first"
              [lazy]="true"
              sortField="invoiceDate"
              [sortOrder]="-1"
              [showCurrentPageReport]="settlementsTableShowCurrentPageReport"
              [showPageLinks]="settlementsTableShowPageLinks"
              [showFirstLastIcon]="settlementsTableShowFirstLastIcon"
              [paginatorStyleClass]="settlementsTablePaginatorStyleClass"
              [totalRecords]="totalRecords()"
              currentPageReportTemplate="{last} de {totalRecords}"
            >
              <ng-template pTemplate="header" let-columns>
                @if (isNarrowViewport()) {
                  <tr>
                    <th>
                      <div class="d-flex align-items-center justify-content-between w-100 gap-2">
                        <span>Liquidación</span>
                        <div class="d-flex align-items-center gap-2">
                          <span
                            class="small cursor-pointer color-download-lucide-icon"
                            [class.opacity-50]="!settlementsSelecteds.length || isDownloading()"
                            [class.pointer-events-none]="
                              !settlementsSelecteds.length || isDownloading()
                            "
                            (click)="
                              !isDownloading() &&
                                settlementsSelecteds.length &&
                                downloadSelectedSettlements()
                            "
                          >
                            @if (isDownloading()) {
                              Descargando...
                            } @else {
                              Descargar todo
                            }
                          </span>
                          <lucide-icon
                            (click)="op2Mobile.toggle($event)"
                            name="help-circle"
                            [size]="16"
                            class="cursor-pointer text-secondary"
                          ></lucide-icon>
                          <p-popover #op2Mobile>
                            <span>Selecciona filas con el checkbox y pulsa «Descargar todo».</span>
                          </p-popover>
                        </div>
                      </div>
                    </th>
                  </tr>
                } @else {
                  <tr>
                    <th
                      style="width: 15%"
                      class="d-none d-md-table-cell d-lg-table-cell d-xl-table-cell"
                    >
                      <div class="d-flex">
                        <p-tableHeaderCheckbox />

                        <lucide-icon
                          (click)="op2.toggle($event)"
                          [name]="isDownloading() ? 'loader' : 'download'"
                          class="color-download-lucide-icon ms-auto cursor-pointer"
                          [class.animate-spin]="isDownloading()"
                          (click)="
                            !isDownloading() &&
                              settlementsSelecteds.length &&
                              downloadSelectedSettlements()
                          "
                          [class.opacity-50]="
                            !settlementsSelecteds ||
                            settlementsSelecteds.length <= 0 ||
                            isDownloading()
                          "
                          [class.pointer-events-none]="
                            !settlementsSelecteds ||
                            settlementsSelecteds.length <= 0 ||
                            isDownloading()
                          "
                        ></lucide-icon>
                        <p-popover #op2>
                          <span>
                            @if (isDownloading()) {
                              Descargando...
                            } @else {
                              Descargar liquidaciones seleccionadas
                            }
                          </span>
                        </p-popover>
                      </div>
                    </th>

                    <th
                      style="width: 21.25%"
                      pSortableColumn="supplyType"
                      class="d-none d-md-table-cell d-lg-table-cell d-xl-table-cell"
                      field="type"
                    >
                      Suministro
                      <p-sortIcon field="supplyType" />
                    </th>
                    <th
                      style="width: 21.25%"
                      class="d-none d-md-table-cell d-lg-table-cell d-xl-table-cell"
                    >
                      Periodo
                    </th>
                    <!-- Ordenación temporalmente deshabilitada: pSortableColumn="totalAmount" -->
                    <th
                      style="width: 21.25%"
                      class="d-none d-md-table-cell d-lg-table-cell d-xl-table-cell text-end"
                    >
                      Importe total
                      <!-- <p-sortIcon field="totalAmount" /> -->
                    </th>
                    <th
                      style="width: 21.25%"
                      class="d-none text-center d-md-table-cell d-lg-table-cell d-xl-table-cell"
                      field="descarga"
                    >
                      Descarga liquidación (PDF)
                    </th>
                  </tr>
                }
              </ng-template>
              <ng-template
                pTemplate="body"
                let-settlement
                let-columns="columns"
                let-rowIndex="rowIndex"
              >
                @if (isNarrowViewport()) {
                  <tr [pSelectableRow]="settlement" [pSelectableRowIndex]="rowIndex">
                    <td [class.home-table-td-fixed]="useFixedTableRowHeight()">
                      <div class="d-flex align-items-center w-100 gap-2">
                        <p-tableCheckbox [value]="settlement" />
                        <div class="flex-grow-1 text-center min-w-0 px-1">
                          <div class="fw-medium text-break">{{ settlement.supplyType }}</div>
                          <small class="text-secondary d-block">
                            {{ dateSpanishFormat(settlement.initDate) }} -
                            {{ dateSpanishFormat(settlement.endDate) }}
                          </small>
                          <small class="text-secondary d-block">
                            {{ spanishFormat(settlement.totalAmount) }} €
                          </small>
                        </div>
                        <p-button
                          [disabled]="isDownloading()"
                          (click)="downloadPdf(settlement)"
                          icon="pi pi-download"
                          [rounded]="true"
                        />
                      </div>
                    </td>
                  </tr>
                } @else {
                  <tr [pSelectableRow]="settlement" [pSelectableRowIndex]="rowIndex">
                    <td [class.home-table-td-fixed]="useFixedTableRowHeight()">
                      <p-tableCheckbox [value]="settlement" />
                    </td>

                    <td [class.home-table-td-fixed]="useFixedTableRowHeight()">
                      {{ settlement.supplyType }}
                    </td>
                    <td [class.home-table-td-fixed]="useFixedTableRowHeight()">
                      {{ dateSpanishFormat(settlement.initDate) }} -
                      {{ dateSpanishFormat(settlement.endDate) }}
                    </td>
                    <td class="text-end" [class.home-table-td-fixed]="useFixedTableRowHeight()">
                      {{ spanishFormat(settlement.totalAmount) }} €
                    </td>
                    <td class="text-center" [class.home-table-td-fixed]="useFixedTableRowHeight()">
                      <p-button
                        [disabled]="isDownloading()"
                        (click)="downloadPdf(settlement)"
                        icon="pi pi-download"
                        [rounded]="true"
                      />
                    </td>
                  </tr>
                }
              </ng-template>
              <ng-template #emptymessage>
                <tr>
                  <td colspan="5">Ningun resultado encontrado.</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        } @else {
          <p-card class="mt-3 mb-3 mt-xl-0 my-xl-0">
            <div class="d-flex fw-bold mb-2 fs-5 align-items-center">
              <div class="w-100"><span class="me-2"> Listado de liquidaciones</span></div>
            </div>
            <div class="skeleton-table" aria-busy="true">
              <div class="skeleton-table-header" aria-hidden="true">
                <p-skeleton width="60%" height="12px" />
                <p-skeleton width="85%" height="12px" />
                <p-skeleton width="75%" height="12px" />
                <div class="d-flex justify-content-center w-100">
                  <p-skeleton width="70%" height="12px" />
                </div>
              </div>
              @for (row of [1, 2, 3, 4, 5, 6, 7, 8]; track row) {
                <div class="skeleton-table-row" aria-hidden="true">
                  <p-skeleton width="18px" height="18px" borderRadius="4px" />
                  <p-skeleton width="85%" height="12px" />
                  <p-skeleton width="75%" height="12px" />
                  <div class="d-flex justify-content-center align-items-center w-100">
                    <p-skeleton shape="circle" size="2.5rem" />
                  </div>
                </div>
              }
            </div>
          </p-card>
        }
      </div>
    }

    @if (hasNextReadingDates()) {
      <section class="next-readings-section" aria-label="Próximas lecturas">
        <div class="next-readings-grid">
          @for (item of nextReadingDates(); track item.supplyType + item.readingDate) {
            <article class="next-reading-card">
              <div class="next-reading-icon">
                <lucide-icon
                  name="CalendarDays"
                  [size]="18"
                  [style.color]="'#134B70'"
                ></lucide-icon>
              </div>
              <div class="next-reading-copy">
                <span class="next-reading-label">Próxima lectura</span>
                <h4 class="next-reading-supply">{{ item.supplyType }}</h4>
                <p class="next-reading-date">{{ dateSpanishFormat(item.readingDate) }}</p>
              </div>
            </article>
          }
        </div>
      </section>
    }

    <div class="tips-section">
      <div class="tips-grid">
        <div *ngFor="let item of consejosRandom" class="tip-insight-card">
          <div class="tip-icon" [style.background-color]="item.color + '14'">
            <lucide-icon [name]="getLucideIcon(item.type)" [size]="18" [style.color]="item.color">
            </lucide-icon>
          </div>
          <div class="tip-copy">
            <h4>{{ item.title }}</h4>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </div>
    @if (isGGM()) {
      <div>
        <p-card class="mb-1">
          <h5 class="">La Oficina Virtual viaja contigo: ¡descarga la app ahora!</h5>

          <div class="app-download-row mt-3">
            @if (platform !== 'Android') {
              <div class="app-download-col">
                <img
                  class="pointer"
                  (click)="openMobileQr('ios')"
                  width="175"
                  src="assets/img/disponible-en-el-app-store-300x104.png"
                  alt="Descargar en App Store"
                />
              </div>
            }
            @if (platform !== 'iOS') {
              <div class="app-download-col">
                <img
                  class="pointer"
                  (click)="openMobileQr('android')"
                  width="200"
                  src="assets/img/googleplay_button_es-300x89.png"
                  alt="Descargar en Google Play"
                />
              </div>
            }
          </div>
        </p-card>
      </div>
    }
  </main>
</div>
